import { useEffect, useCallback, useRef } from 'react';
import { useDeviceDetection } from './useDeviceDetection';

interface MobileOptimizationsOptions {
    enableImageOptimization?: boolean;
    enableScrollOptimization?: boolean;
    enableMemoryManagement?: boolean;
    enableBatteryOptimization?: boolean;
}

export const useMobileOptimizations = (options: MobileOptimizationsOptions = {}) => {
    const {
        enableImageOptimization = true,
        enableScrollOptimization = true,
        enableMemoryManagement = true,
        enableBatteryOptimization = true,
    } = options;

    const { isMobile, isTouchDevice } = useDeviceDetection();
    const intersectionObserver = useRef<IntersectionObserver | null>(null);
    const imageCache = useRef<Set<string>>(new Set());

    // Lazy loading for images
    const setupLazyLoading = useCallback(() => {
        if (!enableImageOptimization || !isMobile) return;

        if (intersectionObserver.current) {
            intersectionObserver.current.disconnect();
        }

        intersectionObserver.current = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const img = entry.target as HTMLImageElement;
                        const src = img.dataset.src;
                        
                        if (src && !imageCache.current.has(src)) {
                            img.src = src;
                            img.removeAttribute('data-src');
                            imageCache.current.add(src);
                            intersectionObserver.current?.unobserve(img);
                        }
                    }
                });
            },
            {
                rootMargin: '50px 0px',
                threshold: 0.1,
            }
        );

        // Observe all images with data-src attribute
        const lazyImages = document.querySelectorAll('img[data-src]');
        lazyImages.forEach((img) => {
            intersectionObserver.current?.observe(img);
        });
    }, [enableImageOptimization, isMobile]);

    // Optimize scroll performance
    const optimizeScrolling = useCallback(() => {
        if (!enableScrollOptimization || !isMobile) return;

        // Add CSS for better scroll performance
        const style = document.createElement('style');
        style.textContent = `
            * {
                -webkit-overflow-scrolling: touch;
                overscroll-behavior: contain;
            }
            
            .scroll-container {
                will-change: scroll-position;
                transform: translateZ(0);
            }
        `;
        document.head.appendChild(style);

        return () => {
            document.head.removeChild(style);
        };
    }, [enableScrollOptimization, isMobile]);

    // Memory management for mobile devices
    const setupMemoryManagement = useCallback(() => {
        if (!enableMemoryManagement || !isMobile) return;

        const handleMemoryPressure = () => {
            // Clear image cache
            imageCache.current.clear();
            
            // Force garbage collection (if available)
            if ((window as any).gc) {
                (window as any).gc();
            }
            
            // Clear unused DOM elements
            const unusedElements = document.querySelectorAll('[data-cleanup="true"]');
            unusedElements.forEach((element) => {
                element.remove();
            });
        };

        // Listen for memory pressure events (if supported)
        if ('memory' in (navigator as any)) {
            const memoryInfo = (navigator as any).memory;
            const usedMemoryRatio = memoryInfo.usedJSHeapSize / memoryInfo.totalJSHeapSize;
            
            if (usedMemoryRatio > 0.8) {
                handleMemoryPressure();
            }
        }

        // Cleanup on page visibility change
        const handleVisibilityChange = () => {
            if (document.hidden) {
                handleMemoryPressure();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [enableMemoryManagement, isMobile]);

    // Battery optimization
    const setupBatteryOptimization = useCallback(() => {
        if (!enableBatteryOptimization || !isMobile) return;

        let batteryOptimizations: (() => void)[] = [];

        const handleBatteryChange = (battery: any) => {
            const isLowBattery = battery.level < 0.2;
            const isCharging = battery.charging;

            if (isLowBattery && !isCharging) {
                // Reduce animations
                const style = document.createElement('style');
                style.id = 'battery-optimizations';
                style.textContent = `
                    *, *::before, *::after {
                        animation-duration: 0.01s !important;
                        animation-iteration-count: 1 !important;
                        transition-duration: 0.01s !important;
                    }
                `;
                document.head.appendChild(style);

                // Reduce update frequency
                const reduceFPS = () => {
                    document.documentElement.style.setProperty('--transition-fast', '0ms');
                    document.documentElement.style.setProperty('--transition-normal', '0ms');
                };

                reduceFPS();
                batteryOptimizations.push(() => {
                    const optimizationStyle = document.getElementById('battery-optimizations');
                    if (optimizationStyle) {
                        optimizationStyle.remove();
                    }
                    document.documentElement.style.removeProperty('--transition-fast');
                    document.documentElement.style.removeProperty('--transition-normal');
                });
            } else {
                // Restore normal performance
                batteryOptimizations.forEach(cleanup => cleanup());
                batteryOptimizations = [];
            }
        };

        // Check battery status (if supported)
        if ('getBattery' in navigator) {
            (navigator as any).getBattery().then((battery: any) => {
                handleBatteryChange(battery);
                battery.addEventListener('chargingchange', () => handleBatteryChange(battery));
                battery.addEventListener('levelchange', () => handleBatteryChange(battery));
            });
        }

        return () => {
            batteryOptimizations.forEach(cleanup => cleanup());
        };
    }, [enableBatteryOptimization, isMobile]);

    // Initialize optimizations
    useEffect(() => {
        if (!isMobile) return;

        const cleanupFunctions = [
            setupLazyLoading(),
            optimizeScrolling(),
            setupMemoryManagement(),
            setupBatteryOptimization(),
        ].filter(Boolean);

        return () => {
            if (intersectionObserver.current) {
                intersectionObserver.current.disconnect();
            }
            cleanupFunctions.forEach((cleanup) => {
                if (typeof cleanup === 'function') {
                    cleanup();
                }
            });
        };
    }, [isMobile, setupLazyLoading, optimizeScrolling, setupMemoryManagement, setupBatteryOptimization]);

    return {
        isMobileOptimized: isMobile,
        isTouchDevice,
        observeImage: (img: HTMLImageElement) => {
            if (intersectionObserver.current) {
                intersectionObserver.current.observe(img);
            }
        },
        unobserveImage: (img: HTMLImageElement) => {
            if (intersectionObserver.current) {
                intersectionObserver.current.unobserve(img);
            }
        },
    };
};